import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * @typedef {object} VolumeDiscountRefs
 * @property {HTMLButtonElement[]} tierButton
 */

/**
 * Lets a shopper pick a quantity tier ("Buy More, Save More") on the product page.
 * Selecting a tier sets the real quantity selector so add-to-cart and checkout
 * use the same quantity that was previewed here.
 *
 * @extends {Component<VolumeDiscountRefs>}
 */
class VolumeDiscountSelector extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    this.#syncFromInput();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  /**
   * Handles a tier button being clicked.
   * @param {{quantity: number}} data
   */
  selectTier({ quantity }) {
    const input = this.#getQuantityInput();
    let qty = Number(quantity) || 1;

    if (input) {
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : null;
      qty = Math.max(min, max ? Math.min(qty, max) : qty);

      const quantitySelector = input.closest('quantity-selector-component');

      if (quantitySelector && typeof quantitySelector.setValue === 'function') {
        quantitySelector.setValue(qty.toString());
        quantitySelector.onQuantityChange?.();
        quantitySelector.updateButtonStates?.();
      } else {
        input.value = qty.toString();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(new QuantitySelectorUpdateEvent(qty));
      }
    }

    this.#highlightTier(qty);
  }

  /**
   * Keeps the highlighted tier in sync when quantity changes elsewhere (e.g. +/- buttons).
   * @param {CustomEvent<{quantity: number}>} event
   */
  #handleQuantityUpdate = (event) => {
    const quantity = event?.detail?.quantity;
    if (typeof quantity !== 'number') return;
    this.#highlightTier(quantity);
  };

  #syncFromInput() {
    const input = this.#getQuantityInput();
    this.#highlightTier(input ? Number(input.value) || 1 : 1);
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getQuantityInput() {
    const scope = this.closest('[id^="ProductInformation-"]') ?? document;
    return scope.querySelector('input[name="quantity"]');
  }

  /**
   * @param {number} quantity
   */
  #highlightTier(quantity) {
    const buttons = this.refs.tierButton ?? [];

    for (const button of buttons) {
      const isSelected = Number(button.dataset.quantity) === quantity;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected.toString());
    }
  }
}

if (!customElements.get('volume-discount-selector')) {
  customElements.define('volume-discount-selector', VolumeDiscountSelector);
}
